import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { formatIQD, formatDateTime } from '../../shared/format.js';
import { DateRangePicker, rangeFor } from './components/DateRangePicker.jsx';
import { OverviewKpis } from './components/OverviewKpis.jsx';
import { HourlyChart } from './components/HourlyChart.jsx';
import { CategoryDonut } from './components/CategoryDonut.jsx';
import { CashierBars } from './components/CashierBars.jsx';
import { TopProductsTable } from './components/TopProductsTable.jsx';
import { LowStockPanel } from './components/LowStockPanel.jsx';
import { InventoryValueTable } from './components/InventoryValueTable.jsx';
import { FinancialSummaryChart } from './components/FinancialSummaryChart.jsx';
import { ExportButton } from './components/ExportButton.jsx';
import { ComprehensiveReport } from './components/ComprehensiveReport.jsx';
import { DiscountsReport } from './components/DiscountsReport.jsx';
import { PurchasesReport } from './components/PurchasesReport.jsx';
import {
  useOverview,
  useRevenueByHour,
  useRevenueByCategory,
  useRevenueByCashier,
  useTopProducts,
  useLowStock,
  useInventoryValue,
  useFinancialSummary,
  useCancelledSales
} from './hooks/useReports.js';

const TABS = [
  { key: 'overview', label: 'نظرة عامة' },
  { key: 'comprehensive', label: 'تقرير شامل' },
  { key: 'sales', label: 'المبيعات' },
  { key: 'purchases', label: 'المشتريات' },
  { key: 'discounts', label: 'الخصومات' },
  { key: 'inventory', label: 'المخزون' },
  { key: 'financial', label: 'المالي' }
];

function Tabs({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 bg-white border border-line rounded-[10px] shadow-card">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={clsx(
            'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
            value === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-paper-2'
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// الخادم يعيد أشكالاً مختلفة: مصفوفة مجرّدة (بعد أن يفكّ مُعترض api
// الاستجابات ذات `items`)، أو كائناً مفاتيحه `buckets` (الإيراد بالساعة)
// أو `warehouses` (قيمة المخزون) أو `rows` (التقارير ذات الإجماليات).
// إغفالها كان يُفرغ أزرار التصدير ويترك الرسوم فارغة رغم وجود بيانات.
function asArray(d) {
  if (Array.isArray(d)) return d;
  return d?.rows || d?.items || d?.buckets || d?.warehouses || d?.data || [];
}

/** نص الفترة الظاهر على الشاشة وفي رأس كل ملف مُصدَّر. */
export function periodLabel(range) {
  const f = String(range?.from || '').slice(0, 10);
  const t = String(range?.to || '').slice(0, 10);
  if (!f && !t) return '';
  return f === t ? f : `${f} ← ${t}`;
}

// الطلبات الفاشلة لا تُرمى (hooks/useReports يبتلعها ويُرجع null) كي لا
// يسقط قسم كامل بسبب طلب واحد. المقابل: لا بد من إشعار ظاهر، وإلا بدت
// الشاشة «فارغة بلا سبب» — وهو ما بلّغ عنه العميل حين كان /top-spenders
// يعيد 404.
function LoadNotice({ queries }) {
  const failed = queries.filter((q) => !q.isPending && !q.isLoading && q.data == null);
  if (!failed.length) return null;
  return (
    <div className="rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] text-[#92400E] text-[12.5px] font-semibold px-3.5 py-2.5">
      تعذّر تحميل بعض بيانات هذا القسم ({failed.length}) — تحقّق من الاتصال أو الصلاحيات ثم أعد المحاولة.
    </div>
  );
}

const hourLabel = (h) => (typeof h === 'number' ? `${String(h).padStart(2, '0')}:00` : String(h ?? ''));

function OverviewTab({ range }) {
  const overview = useOverview(range);
  const hourly = useRevenueByHour(range);
  const donut = useRevenueByCategory(range);
  const hourlyChartRef = useRef(null);
  const donutChartRef = useRef(null);

  const hourlyRows = asArray(hourly.data);
  const donutRows = asArray(donut.data);
  const period = periodLabel(range);

  const hourlyTotals = hourlyRows.reduce(
    (a, r) => {
      a.play += Number(r.play || 0);
      a.cafeteria += Number(r.cafeteria || 0);
      a.toys += Number(r.toys || 0);
      a.total += Number(r.total ?? (Number(r.play || 0) + Number(r.cafeteria || 0) + Number(r.toys || 0)));
      return a;
    },
    { play: 0, cafeteria: 0, toys: 0, total: 0 }
  );
  const donutTotal = donutRows.reduce((s, r) => s + Number(r.amount ?? r.value ?? 0), 0);

  return (
    <div className="space-y-4">
      <LoadNotice queries={[overview, hourly, donut]} />
      <OverviewKpis overview={overview.data} />
      <div className="grid gap-3.5" style={{ gridTemplateColumns: '3fr 2fr' }}>
        <Card
          header="الإيرادات حسب الساعة"
          sub={`${period} — توزيع الإيراد على ساعات اليوم`}
        >
          <div className="flex justify-end mb-2">
            <ExportButton
              rows={hourlyRows}
              columns={[
                { key: 'hour', label: 'الساعة', value: (r) => hourLabel(r.hour) },
                { key: 'play', label: 'لعب', value: (r) => Number(r.play || 0) },
                { key: 'cafeteria', label: 'كافتيريا', value: (r) => Number(r.cafeteria || 0) },
                { key: 'toys', label: 'ألعاب', value: (r) => Number(r.toys || 0) },
                {
                  key: 'total',
                  label: 'الإجمالي',
                  value: (r) => Number(r.total ?? (Number(r.play || 0) + Number(r.cafeteria || 0) + Number(r.toys || 0)))
                }
              ]}
              filename="hourly-revenue"
              title="تقرير الإيرادات حسب الساعة"
              subtitle="المبالغ المقبوضة فعلاً — الفواتير الملغاة مستثناة"
              meta={[`الفترة: ${period}`, `عدد الأيام: ${hourly.data?.days ?? 1}`]}
              totalsRow={[
                'الإجمالي',
                hourlyTotals.play,
                hourlyTotals.cafeteria,
                hourlyTotals.toys,
                hourlyTotals.total
              ]}
              chartRef={hourlyChartRef}
            />
          </div>
          <div ref={hourlyChartRef}>
            <HourlyChart data={hourlyRows} />
          </div>
        </Card>
        <Card header="توزيع الإيراد" sub="حسب القسم">
          <div className="flex justify-end mb-2">
            <ExportButton
              rows={donutRows}
              columns={[
                { key: 'name', label: 'القسم', value: (r) => r.category ?? r.name ?? '' },
                { key: 'count', label: 'عدد العمليات', value: (r) => Number(r.count ?? 0) },
                { key: 'value', label: 'المبلغ', value: (r) => Number(r.amount ?? r.value ?? 0) },
                {
                  key: 'share',
                  label: 'الحصة %',
                  value: (r) => (donutTotal
                    ? `${Math.round((Number(r.amount ?? r.value ?? 0) / donutTotal) * 1000) / 10}%`
                    : '0%')
                }
              ]}
              filename="revenue-by-category"
              title="تقرير توزيع الإيرادات"
              subtitle="الإيراد المقبوض موزّعاً على أقسام نقاط البيع"
              meta={[`الفترة: ${period}`, `الإجمالي: ${formatIQD(donutTotal)}`]}
              totalsRow={['الإجمالي', '', donutTotal, '100%']}
              chartRef={donutChartRef}
              orientation="portrait"
            />
          </div>
          <div ref={donutChartRef}>
            <CategoryDonut data={donutRows} />
          </div>
        </Card>
      </div>
    </div>
  );
}

function SalesTab({ range }) {
  const top = useTopProducts({ ...range, limit: 10 });
  const cashier = useRevenueByCashier(range);
  const cancelled = useCancelledSales(range);
  const topChartRef = useRef(null);
  const cashierChartRef = useRef(null);
  const cancelledTableRef = useRef(null);

  const topRows = asArray(top.data);
  const cashierRows = asArray(cashier.data);
  const cancelledRows = asArray(cancelled.data);
  const period = periodLabel(range);

  const topTotals = topRows.reduce(
    (a, r) => {
      a.qty += Number(r.qty || 0);
      a.revenue += Number(r.revenue || 0);
      return a;
    },
    { qty: 0, revenue: 0 }
  );
  const cashierTotals = cashierRows.reduce(
    (a, r) => {
      a.invoices += Number(r.invoicesCount ?? r.invoices ?? 0);
      a.sales += Number(r.salesTotal ?? r.revenue ?? 0);
      a.refunds += Number(r.refundsTotal ?? 0);
      a.cash += Number(r.expectedCash ?? 0);
      return a;
    },
    { invoices: 0, sales: 0, refunds: 0, cash: 0 }
  );
  const cancelledTotal = cancelledRows.reduce(
    (s, r) => s + Number(r.grandTotal ?? r.totalAmount ?? r.amount ?? 0),
    0
  );

  return (
    <div className="space-y-4">
      <LoadNotice queries={[top, cashier, cancelled]} />
      <div className="grid gap-3.5" style={{ gridTemplateColumns: '3fr 2fr' }}>
        <Card header="أكثر المنتجات مبيعاً" sub="أعلى 10">
          <div className="flex justify-end mb-2">
            <ExportButton
              rows={topRows}
              columns={[
                { key: 'name', label: 'المنتج', value: (r) => r.name || '—' },
                { key: 'qty', label: 'الكمية', value: (r) => Number(r.qty || 0) },
                { key: 'revenue', label: 'الإيراد', value: (r) => Number(r.revenue || 0) },
                { key: 'share', label: 'الحصة %', value: (r) => `${r.percentOfTotal ?? 0}%` }
              ]}
              filename="top-products"
              title="تقرير أكثر المنتجات مبيعاً"
              subtitle="الكميات والإيراد المفوتَر — الفواتير الملغاة مستثناة"
              meta={[`الفترة: ${period}`, `عدد المنتجات: ${topRows.length}`]}
              totalsRow={['الإجمالي', topTotals.qty, topTotals.revenue, '100%']}
              chartRef={topChartRef}
              orientation="portrait"
            />
          </div>
          <div ref={topChartRef}>
            <TopProductsTable data={topRows} />
          </div>
        </Card>
        <Card header="أداء الكاشيرين" sub="مبيعات حسب الكاشير">
          <div className="flex justify-end mb-2">
            <ExportButton
              rows={cashierRows}
              columns={[
                { key: 'cashierName', label: 'الكاشير', value: (r) => r.cashierName || r.name || '—' },
                { key: 'invoicesCount', label: 'الفواتير', value: (r) => Number(r.invoicesCount ?? r.invoices ?? 0) },
                { key: 'salesTotal', label: 'المبيعات', value: (r) => Number(r.salesTotal ?? r.revenue ?? 0) },
                { key: 'refundsTotal', label: 'المرتجعات', value: (r) => Number(r.refundsTotal ?? 0) },
                { key: 'expectedCash', label: 'النقد المتوقّع', value: (r) => Number(r.expectedCash ?? 0) }
              ]}
              filename="revenue-by-cashier"
              title="تقرير أداء الكاشيرين"
              subtitle="المبيعات والمرتجعات والنقد المتوقّع لكل كاشير"
              meta={[`الفترة: ${period}`, `عدد الكاشيرين: ${cashierRows.length}`]}
              totalsRow={[
                'الإجمالي',
                cashierTotals.invoices,
                cashierTotals.sales,
                cashierTotals.refunds,
                cashierTotals.cash
              ]}
              chartRef={cashierChartRef}
            />
          </div>
          <div ref={cashierChartRef}>
            <CashierBars data={cashierRows} />
          </div>
        </Card>
      </div>

      <Card header="تقرير الفواتير الملغاة" sub="ضمن النطاق المحدد — لا تدخل أي إجمالي">
        <div className="flex justify-end mb-2">
          <ExportButton
            rows={cancelledRows}
            columns={[
              { key: 'invoiceNumber', label: 'رقم الفاتورة', value: (r) => r.invoiceNumber || r.number || String(r._id || '').slice(-6) },
              { key: 'createdAt', label: 'تاريخ الفاتورة', value: (r) => formatDateTime(r.createdAt) },
              { key: 'posType', label: 'القسم', value: (r) => r.posTypeLabel || r.posType || '—' },
              { key: 'cashier', label: 'الكاشير', value: (r) => r.cashierName || '—' },
              { key: 'amount', label: 'قيمة الفاتورة', value: (r) => Number(r.grandTotal ?? r.totalAmount ?? r.amount ?? 0) },
              { key: 'cancelledAt', label: 'تاريخ الإلغاء', value: (r) => (r.cancelledAt ? formatDateTime(r.cancelledAt) : '—') },
              { key: 'cancelledBy', label: 'من ألغاها', value: (r) => r.cancelledByName || 'غير مسجَّل' },
              { key: 'reason', label: 'سبب الإلغاء', value: (r) => r.cancelReason || '—' }
            ]}
            filename="cancelled-sales"
            title="تقرير الفواتير الملغاة"
            subtitle="فواتير المبيعات الملغاة — تُعرض ولا تُحذف ولا تدخل الإيراد"
            meta={[`الفترة: ${period}`, `العدد: ${cancelledRows.length}`, `القيمة: ${formatIQD(cancelledTotal)}`]}
            totalsRow={['الإجمالي', '', '', '', cancelledTotal, '', '', '']}
            tableRef={cancelledTableRef}
          />
        </div>
        {cancelledRows.length === 0 ? (
          <div className="text-ink-3 text-center py-10 text-sm">لا فواتير ملغاة</div>
        ) : (
          <div ref={cancelledTableRef} style={{ overflowX: 'auto' }}>
            <table className="w-full text-[13px]">
              <thead className="text-ink-2 text-[12px]">
                <tr className="border-b border-line">
                  <th className="p-2.5 text-start">رقم الفاتورة</th>
                  <th className="p-2.5 text-start">تاريخ الفاتورة</th>
                  <th className="p-2.5 text-start">القسم</th>
                  <th className="p-2.5 text-start">الكاشير</th>
                  <th className="p-2.5 text-start">قيمة الفاتورة</th>
                  <th className="p-2.5 text-start">تاريخ الإلغاء</th>
                  <th className="p-2.5 text-start">من ألغاها</th>
                  <th className="p-2.5 text-start">سبب الإلغاء</th>
                </tr>
              </thead>
              <tbody>
                {cancelledRows.map((r, i) => (
                  <tr key={r._id || r.id || i} className="border-b border-line">
                    <td className="p-2.5 font-mono text-[12px]">
                      {r.invoiceNumber || r.number || String(r._id || '').slice(-6)}
                    </td>
                    <td className="p-2.5 text-ink-3 text-[12px]">{formatDateTime(r.createdAt)}</td>
                    <td className="p-2.5">{r.posTypeLabel || r.posType || '—'}</td>
                    <td className="p-2.5">{r.cashierName || '—'}</td>
                    <td className="p-2.5 font-bold">
                      {formatIQD(r.grandTotal ?? r.totalAmount ?? r.amount ?? 0)}
                    </td>
                    <td className="p-2.5 text-ink-3 text-[12px]">
                      {r.cancelledAt ? formatDateTime(r.cancelledAt) : '—'}
                    </td>
                    <td className="p-2.5 font-semibold">{r.cancelledByName || 'غير مسجَّل'}</td>
                    <td className="p-2.5">
                      <Pill variant="danger">{r.cancelReason || 'بلا سبب مسجَّل'}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-paper-2 font-bold">
                  <td className="p-2.5" colSpan={4}>{`الإجمالي (${cancelledRows.length} فاتورة)`}</td>
                  <td className="p-2.5">{formatIQD(cancelledTotal)}</td>
                  <td className="p-2.5" colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function InventoryTab({ range }) {
  const value = useInventoryValue();
  const lowStock = useLowStock();
  const valueTableRef = useRef(null);
  const lowStockRef = useRef(null);
  const valueRows = asArray(value.data);
  const lowStockRows = asArray(lowStock.data);
  const period = periodLabel(range);

  const invTotals = valueRows.reduce(
    (a, r) => {
      a.products += Number(r.productCount ?? r.products ?? 0);
      a.quantity += Number(r.quantity ?? 0);
      a.value += Number(r.value ?? r.totalValue ?? 0);
      return a;
    },
    { products: 0, quantity: 0, value: 0 }
  );

  return (
    <div className="space-y-4">
      <LoadNotice queries={[value, lowStock]} />
      <Card header="قيمة المخزون" sub="حسب المخزن">
        <div className="flex justify-end mb-2">
          <ExportButton
            rows={valueRows}
            columns={[
              { key: 'warehouse', label: 'المخزن', value: (r) => r.warehouse || r.warehouseName || r.name || '—' },
              { key: 'productCount', label: 'عدد المنتجات', value: (r) => Number(r.productCount ?? r.products ?? 0) },
              { key: 'quantity', label: 'الكمية الإجمالية', value: (r) => Number(r.quantity ?? 0) },
              { key: 'value', label: 'قيمة المخزون', value: (r) => Number(r.value ?? r.totalValue ?? 0) }
            ]}
            filename="inventory-value"
            title="تقرير قيمة المخزون"
            subtitle="الكمية المتاحة وقيمتها بسعر الكلفة لكل مخزن"
            meta={[`تاريخ اللقطة: ${period}`, `عدد المخازن: ${valueRows.length}`]}
            totalsRow={['الإجمالي', invTotals.products, invTotals.quantity, invTotals.value]}
            tableRef={valueTableRef}
            orientation="portrait"
          />
        </div>
        <div ref={valueTableRef}>
          <InventoryValueTable data={valueRows} />
        </div>
      </Card>

      <Card header="تقرير المخزون المنخفض" sub="عناصر تحت الحد الأدنى">
        <div className="flex justify-end mb-2">
          <ExportButton
            rows={lowStockRows}
            columns={[
              { key: 'name', label: 'المنتج', value: (r) => r.name || r.productName || '—' },
              { key: 'sku', label: 'الرمز', value: (r) => r.sku || '' },
              { key: 'stock', label: 'المتبقي', value: (r) => Number(r.current ?? r.stock ?? 0) },
              { key: 'threshold', label: 'الحد الأدنى', value: (r) => Number(r.minStock ?? r.threshold ?? 0) },
              { key: 'warehouse', label: 'المخزن', value: (r) => r.warehouse || r.warehouseName || '—' }
            ]}
            filename="low-stock"
            title="تقرير المخزون المنخفض"
            subtitle="منتجات وصلت أو نزلت تحت حد التنبيه"
            meta={[`تاريخ اللقطة: ${period}`, `عدد الأصناف: ${lowStockRows.length}`]}
            tableRef={lowStockRef}
            orientation="portrait"
          />
        </div>
        <div ref={lowStockRef}>
          <LowStockPanel data={lowStockRows} />
        </div>
      </Card>
    </div>
  );
}

function FinancialTab({ range }) {
  const summary = useFinancialSummary(range);
  const donut = useRevenueByCategory(range);
  const trendChartRef = useRef(null);
  const donutChartRef = useRef(null);

  const sumRows = asArray(summary.data?.trend || summary.data?.daily || summary.data);
  const donutRows = asArray(donut.data);
  const period = periodLabel(range);

  const fin = summary.data?.totals || sumRows.reduce(
    (a, r) => {
      a.revenue += Number(r.revenue || 0);
      a.expense += Number(r.expense || 0);
      return a;
    },
    { revenue: 0, expense: 0, net: 0 }
  );
  const net = fin.net ?? (Number(fin.revenue || 0) - Number(fin.expense || 0));
  const donutTotal = donutRows.reduce((s, r) => s + Number(r.amount ?? r.value ?? 0), 0);

  return (
    <div className="space-y-4">
      <LoadNotice queries={[summary, donut]} />
      <Card header="تقرير المصاريف مقابل الإيرادات" sub="منحنى يومي">
        <div className="flex justify-end mb-2">
          <ExportButton
            rows={sumRows}
            columns={[
              { key: 'date', label: 'التاريخ', value: (r) => r.date || '' },
              { key: 'revenue', label: 'الإيراد', value: (r) => Number(r.revenue || 0) },
              { key: 'expense', label: 'المصاريف', value: (r) => Number(r.expense || 0) },
              { key: 'net', label: 'الصافي', value: (r) => Number(r.net ?? (Number(r.revenue || 0) - Number(r.expense || 0))) }
            ]}
            filename="financial-summary"
            title="تقرير المصاريف مقابل الإيرادات"
            subtitle="الإيراد المقبوض مقابل المصاريف المعتمدة — يوماً بيوم"
            meta={[
              `الفترة: ${period}`,
              `الإيراد: ${formatIQD(fin.revenue || 0)}`,
              `المصاريف: ${formatIQD(fin.expense || 0)}`,
              `الصافي: ${formatIQD(net)}`
            ]}
            totalsRow={['الإجمالي', Number(fin.revenue || 0), Number(fin.expense || 0), Number(net)]}
            chartRef={trendChartRef}
            orientation="portrait"
          />
        </div>
        <div ref={trendChartRef}>
          <FinancialSummaryChart data={summary.data} />
        </div>
      </Card>

      <Card header="تقرير توزيع الإيراد" sub="حسب القسم">
        <div className="flex justify-end mb-2">
          <ExportButton
            rows={donutRows}
            columns={[
              { key: 'name', label: 'القسم', value: (r) => r.category ?? r.name ?? '' },
              { key: 'count', label: 'عدد العمليات', value: (r) => Number(r.count ?? 0) },
              { key: 'value', label: 'المبلغ', value: (r) => Number(r.amount ?? r.value ?? 0) },
              {
                key: 'share',
                label: 'الحصة %',
                value: (r) => (donutTotal
                  ? `${Math.round((Number(r.amount ?? r.value ?? 0) / donutTotal) * 1000) / 10}%`
                  : '0%')
              }
            ]}
            filename="revenue-breakdown"
            title="تقرير توزيع الإيراد"
            subtitle="الإيراد المقبوض موزّعاً على أقسام نقاط البيع"
            meta={[`الفترة: ${period}`, `الإجمالي: ${formatIQD(donutTotal)}`]}
            totalsRow={['الإجمالي', '', donutTotal, '100%']}
            chartRef={donutChartRef}
            orientation="portrait"
          />
        </div>
        <div ref={donutChartRef}>
          <CategoryDonut data={donutRows} />
        </div>
      </Card>
    </div>
  );
}

export function ReportsScreen() {
  const [tab, setTab] = useState('overview');
  const [rangeMeta, setRangeMeta] = useState({ preset: 'today', ...rangeFor('today') });
  const range = useMemo(() => ({ from: rangeMeta.from, to: rangeMeta.to }), [rangeMeta]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">التقارير والإحصائيات</h2>
          <div className="text-ink-3 text-xs mt-0.5">تحليلات النشاط والمبيعات والمشتريات والمخزون</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangePicker value={rangeMeta} onChange={setRangeMeta} />
          <Tabs value={tab} onChange={setTab} />
        </div>
      </div>

      {tab === 'overview' ? <OverviewTab range={range} /> : null}
      {tab === 'comprehensive' ? <ComprehensiveReport range={range} /> : null}
      {tab === 'sales' ? <SalesTab range={range} /> : null}
      {tab === 'purchases' ? <PurchasesReport range={range} /> : null}
      {tab === 'discounts' ? <DiscountsReport range={range} /> : null}
      {tab === 'inventory' ? <InventoryTab range={range} /> : null}
      {tab === 'financial' ? <FinancialTab range={range} /> : null}
    </div>
  );
}
